import React, { PropsWithChildren, Suspense, memo, useMemo } from "react";
import Draggable from "react-draggable";
import styled from "styled-components";

import Modal, { ModalProps } from "../../../ModalController/Modal";
import useKeyBind from "../../../../hooks/useKeyBind";
import {
	Style,
	StyledColumn,
	BaseElementIdEnum,
	SuspenseLoader,
} from "../../../common";

import Header from "../Header";
import Footer from "../Footer";

const Wrap = styled(StyledColumn)`
	border-radius: 10px;
`;

/** @deprecated */
const BaseCardModal: React.FC<BaseCardModal.Props> = ({
	title,
	dimmer,
	shadow = !dimmer,
	draggable = true,
	styles,
	children,
	header,
	headerDivider,
	footer,
	footerDivider,
	textClose,
	textSave,
	buttonSave,
	buttonClose,
	buttonCross,
	id,
	onClose,
	onSave,

	...props
}) => {
	useKeyBind(["Escape"], (event) => {
		onClose(event);
		event.preventDefault();
		event.stopPropagation();
	});

	useKeyBind(["Enter"], (event) => {
		onSave(event);
		event.preventDefault();
		event.stopPropagation();
	});

	const content = useMemo(
		() => (
			<Wrap
				id={id ?? BaseElementIdEnum.MODAL}
				shadow={shadow ? "0px 3px 14px rgba(0,0,0,0.2)" : "none"}
				position="absolute"
				bgC="#ffffff"
				{...styles}
			>
				<Header
					title={title}
					onClose={onClose}
					draggable={draggable}
					divider={headerDivider}
					buttonCross={buttonCross}
				>
					{header}
				</Header>
				<Suspense fallback={<SuspenseLoader />}>{children}</Suspense>

				<Footer
					onClose={onClose}
					onSave={onSave}
					divider={footerDivider}
					textClose={textClose}
					textSave={textSave}
					buttonSave={buttonSave}
					buttonClose={buttonClose}
				>
					{footer}
				</Footer>
			</Wrap>
		),
		[
			buttonClose,
			buttonCross,
			buttonSave,
			children,
			draggable,

			footer,
			footerDivider,
			header,
			headerDivider,
			id,
			onClose,
			onSave,
			shadow,
			styles,
			textClose,
			textSave,
			title,
		],
	);

	const elem = useMemo(
		() =>
			draggable ? (
				<Draggable handle=".handle">{content}</Draggable>
			) : (
				content
			),
		[content, draggable],
	);

	return (
		<Modal dimmer={dimmer} {...props}>
			{elem}
		</Modal>
	);
};

declare namespace BaseCardModal {
	interface Props
		extends ModalProps,
			PropsWithChildren,
			Omit<Footer.Props, "divider">,
			Omit<Header.Props, "divider"> {
		footer?: React.ReactNode;
		header?: React.ReactNode;
		footerDivider?: boolean;
		headerDivider?: boolean;

		shadow?: boolean | string;
		draggable?: boolean;
		styles?: Style.FlexType & Style.DistributeType;
		id?: string;
		tabId?: string[];
	}

	type Event =
		| React.MouseEvent<HTMLButtonElement, MouseEvent>
		| KeyboardEvent
		| undefined;
}
/** @deprecated */
export const CardModal = memo(BaseCardModal);
export default BaseCardModal;
